import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { fetchVendors, deleteUser, formatVendorRow } from "../services/userService";
import arrowdown from '../assets/arrow-down-simple.png';
import eye from '../assets/eye-svgrepo-com (2).svg';
import edit from '../assets/edit-3-svgrepo-com.svg';
import deleteicon from '../assets/delete-2-svgrepo-com.svg';
import leftarrow from '../assets/left-arrow-backup-2-svgrepo-com.svg';
import rightarrow from '../assets/right-arrow-backup-2-svgrepo-com.svg';
import { FaChevronDown } from "react-icons/fa";
import CustomDarkCheckbox from './CustomDarkCheckbox';

const VendorsTable = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [vendorsData, setVendorsData] = useState([]);

  const [openItems, setOpenItems] = useState({});
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);

  const vendorsPerPage = 10;

  const fetchVendorsData = async (page) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchVendors(page, vendorsPerPage);

      if (response.status === "success" && Array.isArray(response.data)) {
        const formatted = response.data.map(formatVendorRow);

        setVendorsData(formatted);
        setTotalPages(response.paginationResult?.numberOfPages || 1);
      } else {
        setVendorsData([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
      setError("Failed to load vendors. Please check your network connection.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendorsData(currentPage);
  }, [currentPage]);

  // ✅ تحديث تلقائي كل 5 دقايق
  useEffect(() => {
    const interval = setInterval(() => {
      fetchVendorsData(currentPage);
    }, 5 * 60 * 1000); // كل 5 دقايق
    return () => clearInterval(interval);
  }, [currentPage]);

  // 🗑️ Open delete modal
  const openDeleteModal = (vendor) => {
    setVendorToDelete(vendor);
    setShowDeleteModal(true);
  };

  // ✅ Confirm and delete vendor
  const confirmDelete = async () => {
    if (!vendorToDelete) return;
    try {
      await deleteUser(vendorToDelete.id);

      setVendorsData((prev) => prev.filter((v) => v.id !== vendorToDelete.id));
    } catch (error) {
      console.error("Error deleting vendor:", error);
      setError("Failed to delete vendor. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setVendorToDelete(null);
    }
  };

  // ✅ Edit vendor
  const handleEditVendor = (vendor) => {
    navigate(`/Users/edit-User/${vendor.id}`, { state: { activeTab: "Vendors", user: vendor } });
  };

  // ✅ Export CSV
  const exportToCSV = () => {
    if (!vendorsData || vendorsData.length === 0) return;
    const headers = Object.keys(vendorsData[0]).join(",");
    const rows = vendorsData.map(v => Object.values(v).join(",")).join("\n");
    const blob = new Blob([`${headers}\n${rows}`], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Vendors.csv";
    link.click();
  };

  // ✅ Select vendors
  const toggleSelectAll = () => {
    if (selectedVendors.length === vendorsData.length) {
      setSelectedVendors([]);
    } else {
      setSelectedVendors(vendorsData.map(v => v.id));
    }
  };

  const toggleSelectVendor = (id) => {
    setSelectedVendors(prev =>
      prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
    );
  };

  // ✅ Toggle details in mobile
  const toggleVendor = (id) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // ✅ Page select
  const handlePageSelect = (page) => {
    setCurrentPage(page);
    setIsOpen(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <p className="text-gray-500 dark:text-gray-300">Loading vendors...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-48">
        <p className="text-red-500 dark:text-red-300">Error: {error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Table view */}
      <div className="overflow-hidden rounded-xl border border-gray-300 dark:border-[#3D3D3D] shadow-md">
        <table className="min-w-full border-collapse bg-white dark:bg-[#1A1A1B] hidden sm:table md:table">
          <thead className="min-w-full bg-gray-100 border-b border-[#E7E7E7] dark:bg-black dark:border-[#3D3D3D] dark:text-gray-400">
            <tr>
              <th className="flex justify-between text-left px-4 py-4 text-black text-sm font-medium select-none dark:text-gray-200">
                <div className="flex gap-4">
                  <CustomDarkCheckbox
                    checked={
                      selectedVendors.length === vendorsData.length &&
                      vendorsData.length > 0
                    }
                    onChange={toggleSelectAll}
                  />
                  Vendor
                </div>
              </th>
              <th className="text-left px-4 py-3 text-black text-sm font-medium dark:text-gray-200">
                Contact
              </th>
              <th className="text-left px-4 py-3 text-black text-sm font-medium dark:text-gray-200">
                Sales
              </th>
              <th className="text-left px-4 py-3 text-black text-sm font-medium dark:text-gray-200">
                Contact Person
              </th>
              <th className="text-left px-4 py-3 text-black text-sm font-medium dark:text-gray-200">
                Orders
              </th>
              <th className="text-left px-4 py-3 text-black text-sm font-medium dark:text-gray-200">
                Address
              </th>
              <th className="text-left px-4 py-3 text-black text-sm font-medium dark:text-gray-200">
                Join Date
              </th>
              <th className="text-left px-4 py-3 text-black text-sm font-medium dark:text-gray-200">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {vendorsData.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-4 text-center text-gray-500 dark:text-gray-200"
                >
                  No Vendors found.
                </td>
              </tr>
            ) : (
              vendorsData.map((c) => (
                <tr
                  key={c.id}
                  className="border-b border-gray-200 hover:bg-gray-50 dark:hover:bg-[#2D2D2D] dark:border-[#3D3D3D]"
                >
                  <td className="flex pl-4 items-center">
                    <CustomDarkCheckbox
                      checked={selectedVendors.includes(c.id)}
                      onChange={() => toggleSelectVendor(c.id)}
                    />
                    <div className="flex items-center gap-3 px-4 py-3 text-gray-800 text-sm">
                      <div>
                        <p className="text-xs agarly-gradient-text">
                          ID {c.id}
                        </p>
                        <p className="text-gray-700 inline dark:text-gray-300">
                          {c.name}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-sm dark:text-gray-300">
                    {c.email} <br />
                    {c.phone}
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-sm dark:text-gray-300">
                    $ {c.sales}
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-sm dark:text-gray-300">
                    {c.contactperson}
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-sm max-w-[150px] overflow-hidden dark:text-gray-300">
                    {c.orders}
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-sm dark:text-gray-300">
                    {c.address}
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-sm dark:text-gray-300">
                    {new Date(c.joindate).toLocaleDateString("en-GB")} <br />
                    {new Date(c.joindate).toLocaleTimeString("en-GB", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                  </td>
                  <td className="items-center px-4 py-3 text-gray-700 text-sm flex gap-4 dark:text-gray-300">
                    <Link
                      to={`/Users/Vendors/view-Vendor/${c.id}`}
                      state={{ Vendor: c }}
                    >
                      <img src={eye} alt="eye" className="w-5 h-5 inline" />
                    </Link>
                    <button onClick={() => handleEditVendor(c)}>
                      <img src={edit} alt="edit" className="w-5 h-5 inline" />
                    </button>
                    <button onClick={() => openDeleteModal(c)}>
                      <img
                        src={deleteicon}
                        alt="delete"
                        className="w-5 h-5 inline"
                      />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-0 z-50">
          <div className="bg-white rounded-lg p-6 w-80 text-center shadow-lg dark:bg-black">
            <p className="mb-4 text-gray-700 dark:text-gray-300">
              Are you sure you want to delete{" "}
              <strong>{vendorToDelete.name}</strong>?
            </p>
            <div className="flex justify-between">
              <button
                className="px-4 py-2 rounded bg-gray-300 text-gray-700 hover:bg-gray-400"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Mobile view */}
      <div className="md:hidden bg-white flex flex-col items-start p-5 gap-6 w-full border border-[#E7E7E7] rounded-2xl shadow-md mt-6 dark:bg-[#1A1A1B]">
        {vendorsData.map((c) => (
          <div key={c.id} className="relative w-full border-b border-gray-200 dark:bg-[#1A1A1B]">
            <div className="flex justify-between items-center w-full py-3 px-4">
              <div className="flex gap-3 items-center">
                <div>
                  <p className="text-xs agarly-gradient-text">ID {c.id}</p>
                  <p className="dark:text-gray-300">{c.name}</p>
                </div>
              </div>
              <button onClick={() => toggleVendor(c.id)}>
                <img src={arrowdown} alt="toggle" className={`w-5 h-5 transition-transform ${openItems[c.id] ? "rotate-180" : ""}`} />
              </button>
            </div>

            {openItems[c.id] && (
              <div className="bg-white w-full rounded-lg p-4 mt-1 dark:bg-[#1A1A1B]">
                <div className="text-gray-900 text-sm mb-2 flex gap-2">
                  <span className="text-gray-500 dark:text-gray-300">Contact: </span>
                  <div className="flex flex-col flex-1 break-words dark:text-gray-400">{c.email} <br/> {c.phone}</div>
                </div>
                <div className="text-gray-900 text-sm mb-2 gap-2 dark:text-gray-400"><span className="text-gray-500 dark:text-gray-300">sales: </span>${c.sales}</div>
                <div className="text-gray-900 text-sm mb-2 gap-2 dark:text-gray-400"><span className="text-gray-500 dark:text-gray-300">Contact Person: </span>{c.contactperson}</div>
                <div className="text-gray-900 text-sm mb-2 gap-2 dark:text-gray-400"><span className="text-gray-500 dark:text-gray-300">Orders QTY: </span>{c.orders}</div>
                <div className="text-gray-900 text-sm mb-2 flex gap-2">
                  <span className="text-gray-500 dark:text-gray-300">Address: </span>
                  <span className="flex flex-col flex-1 break-words dark:text-gray-400">{c.address}</span>
                </div>
                <div className="flex gap-2 text-gray-900 text-sm mb-2">
                  <span className="text-gray-500 dark:text-gray-300">Actions: </span>
                  <div className="flex gap-4">
                    <Link to={`/Users/Vendors/view-Vendor/${c.id}`} state={{ Vendor: c }}>
                      <img src={eye} alt="eye" className="w-5 h-5 inline" />
                    </Link>
                    <button onClick={() => handleEditVendor(c)}>
                      <img src={edit} alt="edit" className="w-5 h-5 inline" />
                    </button>
                    <button onClick={() => openDeleteModal(c)}>
                      <img src={deleteicon} alt="delete" className="w-4 h-4 inline" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ✅ Pagination */}
<div className="flex justify-between items-center mt-4">
  <p className="text-sm text-gray-700 dark:text-gray-400">
    <span className="dark:text-gray-200">{currentPage}</span> of {totalPages} Pages
  </p>
  <div className="flex gap-2 items-center">
    <button
      className="border-2 px-1 py-1 rounded-xl disabled:opacity-50"
      disabled={currentPage === 1}
      onClick={() => setCurrentPage((p) => p - 1)}
    >
      <img src={leftarrow} alt="previous" className="w-5 h-5" />
    </button>

    <div className="relative inline-block">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 border px-2 py-1 rounded-xl dark:text-gray-200"
      >
        {currentPage} <FaChevronDown />
      </button>

      {isOpen && (
        <div className="absolute bg-white border rounded-md shadow z-10 mb-10">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <div
              key={p}
              onClick={() => handlePageSelect(p)}
              className={`px-3 py-1 cursor-pointer ${
                p === currentPage ? "bg-gray-200 font-semibold" : ""
              }`}
            >
              {p}
            </div>
          ))}
        </div>
      )}
    </div>

    <button
      className="border-2 px-1 py-1 rounded-xl disabled:opacity-50"
      disabled={currentPage === totalPages}
      onClick={() => setCurrentPage((p) => p + 1)}
    >
      <img src={rightarrow} alt="next" className="w-5 h-5" />
    </button>
  </div>
</div>
    </div>
  );
};

export default VendorsTable;